import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { frontendRoutes } from '../Constants/constants';
import { clearPropertyContext } from '../redux/action/action';
import { loadPropertyContext } from '../services/propertyResolution';
import {
  clearStoredPropertyContext,
  getStoredVisitId,
  getTourArrivalSource,
  logPropertyResolutionFailure,
  createPropertyError,
} from '../utils/propertyRecovery';
import { PropertyError, PropertyInfo } from '../types/property';

type LoadPropertyArgs = {
  propertyId: string | number;
  customerId?: string | number;
  propertyName?: string;
  isRetry?: boolean;
  forceRehydrate?: boolean;
};

type ReportArgs = {
  propertyId?: string | number;
  propertyName?: string;
  customerId?: string | number;
  isRetry?: boolean;
};

export const usePropertyResolution = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams<{ builder_name?: string }>();
  const builderId = useSelector((state: any) => state.builder.builder_id);

  const [error, setError] = useState<PropertyError | null>(null);
  const [isLoadingProperty, setIsLoadingProperty] = useState(false);

  const clearError = useCallback(() => setError(null), []);

  const reportError = useCallback((rawError: unknown, args: ReportArgs = {}) => {
    const propertyError = createPropertyError(rawError);

    logPropertyResolutionFailure({
      error: propertyError,
      propertyId: args.propertyId,
      propertyName: args.propertyName,
      builderId,
      customerId: args.customerId,
      visitId: getStoredVisitId(),
      arrivalSource: getTourArrivalSource(args.isRetry),
      attempt: args.isRetry ? 2 : 1,
    });
    setError(propertyError);

    return propertyError;
  }, [builderId]);

  const loadProperty = useCallback(async ({
    propertyId,
    customerId,
    propertyName,
    isRetry = false,
    forceRehydrate,
  }: LoadPropertyArgs): Promise<PropertyInfo | null> => {
    setIsLoadingProperty(true);

    try {
      const { property } = await loadPropertyContext({
        propertyId,
        builderId,
        customerId,
        arrivalSource: getTourArrivalSource(isRetry),
        forceRehydrate: forceRehydrate ?? isRetry,
      });

      setError(null);
      return property;
    } catch (rawError) {
      reportError(rawError, { propertyId, propertyName, customerId, isRetry });
      return null;
    } finally {
      setIsLoadingProperty(false);
    }
  }, [builderId, reportError]);

  const findOtherProperties = useCallback(() => {
    clearStoredPropertyContext();
    dispatch(clearPropertyContext());
    history.push(frontendRoutes.chooseCommunity(params.builder_name).path);
  }, [dispatch, history, params.builder_name]);

  return {
    error,
    isLoadingProperty,
    clearError,
    reportError,
    loadProperty,
    findOtherProperties,
  };
};
